// components/Appointment/index.jsx
import React, {useEffect, useMemo} from 'react'
import {Box, Stack, Tab, Tabs} from '@mui/material'
import clsx from 'clsx'
import {useState} from 'react'
import {useStyles} from './styles'
import TabPanel from './TabPanel'
import Information from './information/Information'
import Confirmation from './confirmation/Confirmation'
import Details from './details/Details'
import {useTranslation} from 'react-i18next'
import {useForm} from 'react-hook-form'
import apiService from '../../services/api'; // Import the new service
import {yupResolver} from '@hookform/resolvers/yup'
import {appointmentSchema} from '../../utils/formValidation'
import moment from 'moment'
import {useLocation} from 'react-router-dom'
import {useNavigate} from 'react-router-dom'
import i18n from "i18next";

const Appointment = () => {
    const classes = useStyles()
    const [currentTab, setCurrentTab] = useState(0)
    const {t} = useTranslation()
    const [additionalService, setAdditionalService] = useState([])
    const [selectedTintGlass, setSelectedTintGlass] = useState(null)
    const [serverError, setServerError] = useState('')
    const [open, setOpen] = useState(false)
    const [selectedServices, setSelectedServices] = useState([])
    const [availableTimes, setAvailableTimes] = useState([])
    const location = useLocation()
    const {state} = location
    const navigate = useNavigate()

    // Add this useEffect to scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    // Also scroll to top when tab changes
    useEffect(() => {
        window.scrollTo(0, 0)
    }, [currentTab])

    const nextButtonClassName = clsx(
        classes.btn,
        currentTab === 0 ? classes.infoTab : '',
        currentTab === 1 ? classes.detailTab : '',
        currentTab === 2 ? classes.confirmTab : '',
    )

    const {
        register,
        handleSubmit,
        setError,
        watch,
        clearErrors,
        control,
        setValue,
        reset,
        trigger,
        formState: {errors, isValid},
    } = useForm({
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            date: new Date(),
            time: '',
        },
        mode: 'all',
        resolver: yupResolver(appointmentSchema),
    })

    const dateWatcher = watch('date')
    const timeWatcher = watch('time')
    const firstName = watch('firstName')
    const lastName = watch('lastName')
    const email = watch('email')
    const phoneNumber = watch('phoneNumber')

    const selectedDate = useMemo(() => moment(dateWatcher, 'YYYY-MM-DD'), [dateWatcher])
    const selectedTime = useMemo(() => moment(timeWatcher, 'HH:mm'), [timeWatcher])

    // Add this function before the fetchData useEffect
    const navigateToTintedGlass = () => {
        const pathSegments = window.location.pathname.split('/');
        const currentLocale = pathSegments[1] || 'en';
        navigate(`/${currentLocale}/tinted-glass`);
    }

    // Fetch data on component mount
    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            // Reset form and state
            reset({
                firstName: '',
                lastName: '',
                email: '',
                phoneNumber: '',
                date: new Date(),
                time: '',
            })
            setSelectedServices([])
            setAvailableTimes([])
            setServerError('')

            // Fetch additional services
            const addResponse = await apiService.getAdditionalServices(); // You might need to add this method
            setAdditionalService(addResponse || [])

            // Select tint glass to reset session
            const tintId = state?.tintedGlass?.id
            if (tintId) {
                const tintResponse = await apiService.getTintGlassService(tintId); // You might need to add this method
                setSelectedTintGlass(tintResponse)
                console.log('Calling select-tint-glass on page load, tintId:', tintId)

                // This now uses headers instead of cookies - works in Safari!
                await apiService.selectTintGlass(tintId)
                console.log('Tint glass selected successfully')
            } else {
                // Redirect if no tintId is provided
                console.error('No tintId provided, redirecting to tinted glass page')
                navigateToTintedGlass()
            }
        } catch (error) {
            console.error('Error in fetchData:', error)
            setServerError('Failed to load data')
            if (error.response?.status >= 500) {
                navigateToTintedGlass()
            }
        }
    }

    // Clear errors when date/time changes
    useEffect(() => {
        if (serverError && dateWatcher) {
            clearErrors('date')
        }
        if (serverError && timeWatcher) {
            clearErrors('time')
        }
    }, [dateWatcher, timeWatcher, serverError])

    const submitUserInfo = async () => {
        console.log('submitUserInfo called with data:', {firstName, lastName, email, phoneNumber})
        try {
            await apiService.setUserInfo({
                name: `${firstName} ${lastName}`.trim(),
                email,
                phone: phoneNumber.replaceAll(/[+() -]/g, ''),
            })
            console.log('set-user-info API call successful')
            return true
        } catch (error) {
            console.error('Error setting user info:', error)
            setServerError(error.response?.data?.error || 'Failed to set user info')
            return false
        }
    }

    const sendForm = async () => {
        try {
            console.log('Calling preconfirm API')
            await apiService.preconfirm(i18n.language)
            console.log('preconfirm API call successful')
            setOpen(true)
            setSelectedServices([])
            reset({
                firstName: '',
                lastName: '',
                email: '',
                phoneNumber: '',
                date: new Date(),
                time: '',
            })
            // Clear session after successful booking
            apiService.clearSession()
        } catch (error) {
            console.error('Error in preconfirm:', error)
            setServerError(error.response?.data?.error || 'Failed to book appointment')
        }
    }

    const handleNext = async () => {
        console.log('handleNext called, currentTab:', currentTab, 'form errors:', errors)
        if (currentTab === 0) {
            const isValid = await trigger(['date', 'time'])
            if (isValid && availableTimes.includes(timeWatcher)) {
                console.log('Details tab valid, moving to Information tab')
                setCurrentTab((prevTab) => prevTab + 1)
            } else {
                console.log('Details tab validation failed')
            }
        } else if (currentTab === 1) {
            const isValid = await trigger(['firstName', 'lastName', 'email', 'phoneNumber'])
            console.log('Information tab validation, isValid:', isValid)
            if (isValid) {
                const success = await submitUserInfo()
                if (success) {
                    console.log('Moving to Confirmation tab')
                    setCurrentTab((prevTab) => prevTab + 1)
                }
            } else {
                console.log('Information tab validation failed')
            }
        } else if (currentTab === 2) {
            console.log('Submitting confirmation')
            handleSubmit(sendForm)()
        }
    }

    const handleTabChange = async (event, newTab) => {
        console.log('handleTabChange called, newTab:', newTab)
        if (newTab === 0) {
            const tintId = state?.tintedGlass?.id
            if (tintId) {
                try {
                    console.log('Calling select-tint-glass on Details tab click, tintId:', tintId)
                    await apiService.selectTintGlass(tintId)
                    console.log('select-tint-glass API call successful on tab change')
                    // Reset form and state for Details tab
                    reset({
                        firstName: '',
                        lastName: '',
                        email: '',
                        phoneNumber: '',
                        date: new Date(),
                        time: '',
                    })
                    setSelectedServices([])
                    setAvailableTimes([])
                    setServerError('')
                } catch (error) {
                    console.error('Error resetting session on Details tab click:', error)
                    setServerError('Failed to reset session')
                }
            }
            setCurrentTab(newTab)
        } else if (newTab === 1) {
            const isValid = await trigger(['date', 'time'])
            if (isValid && availableTimes.includes(timeWatcher)) {
                setCurrentTab(newTab)
            }
        } else if (newTab === 2) {
            const isDetailsValid = await trigger(['date', 'time'])
            const isInfoValid = await trigger(['firstName', 'lastName', 'email', 'phoneNumber'])
            if (isDetailsValid && availableTimes.includes(timeWatcher) && isInfoValid) {
                const success = await submitUserInfo()
                if (success) {
                    setCurrentTab(newTab)
                }
            }
        }
    }

    const fetchAvailableTimes = async () => {
        if (!selectedDate.isValid()) {
            setError('date', {message: t('form.invalid_date')})
            return
        }
        try {
            const dateStr = selectedDate.format('YYYY-MM-DD')
            const times = await apiService.getAvailableTimes(dateStr)
            setAvailableTimes(Array.isArray(times) ? times : [])
            clearErrors('date')
        } catch (error) {
            setServerError(error.response?.data?.error || 'Error fetching available times')
            setAvailableTimes([])
        }
    }

    // Add service to appointment
    const addService = async (serviceId) => {
        try {
            await apiService.addAdditionalService(serviceId);
            // Refresh the appointment information
            const info = await apiService.getInformation();
            // Update your state with the new services
            setSelectedServices(info.services || []);
        } catch (error) {
            console.error('Error adding service:', error);
            setServerError('Failed to add service');
        }
    };

    // Remove service from appointment
    const removeService = async (serviceId) => {
        try {
            await apiService.removeAdditionalService(serviceId);
            // Refresh the appointment information
            const info = await apiService.getInformation();
            // Update your state with the new services
            setSelectedServices(info.services || []);
        } catch (error) {
            console.error('Error removing service:', error);
            setServerError('Failed to remove service');
        }
    };

    return (
        <Box className={clsx(classes.container, classes.appointment)}>
            <Box sx={{display: 'contents'}}>
                <Tabs
                    value={currentTab}
                    onChange={handleTabChange}
                    aria-label="secondary tabs example"
                    variant={'scrollable'}
                    textColor="secondary"
                    indicatorColor="secondary"
                    className={classes.tabs}
                    sx={{
                        display: 'flex !important',
                        justifyContent: 'center!important',
                    }}
                    TabIndicatorProps={{
                        sx: {backgroundColor: 'unset !important'},
                    }}
                >
                    <Tab
                        label={t('appointment.details')}
                        className={clsx(
                            classes.tabTitle,
                            classes.detailTab,
                            currentTab === 0 ? classes.activeTab : classes.prevTab,
                        )}
                    />
                    <Tab
                        label={t('appointment.information')}
                        className={clsx(
                            classes.tabTitle,
                            classes.infoTab,
                            currentTab === 1
                                ? classes.activeTab
                                : currentTab === 0
                                    ? classes.nextTab
                                    : classes.prevTab,
                        )}
                        disabled={!(availableTimes.includes(timeWatcher) && !errors.date && !errors.time)}
                    />
                    <Tab
                        label={t('appointment.confirmation')}
                        className={clsx(
                            classes.tabTitle,
                            classes.confirmTab,
                            currentTab === 2 ? classes.activeTab : classes.nextTab,
                        )}
                        disabled={!isValid}
                    />
                </Tabs>
                <Box>
                    <TabPanel value={currentTab} index={0}>
                        <Details
                            setValue={setValue}
                            setError={setError}
                            additionalServices={additionalService}
                            serverError={serverError}
                            selectedTintGlass={selectedTintGlass}
                            errors={errors}
                            register={register}
                            handleNext={handleNext}
                            control={control}
                            selectedServices={selectedServices}
                            setSelectedServices={setSelectedServices}
                            selectedDate={selectedDate}
                            selectedTime={selectedTime}
                            availableTimes={availableTimes}
                            fetchAvailableTimes={fetchAvailableTimes}
                            resetTimeForm={() => {
                                setValue('time', '')
                                setAvailableTimes([])
                            }}
                            addService={addService}
                            removeService={removeService}
                        />
                    </TabPanel>
                    <TabPanel value={currentTab} index={1}>
                        <Information
                            setValue={setValue}
                            errors={errors}
                            register={register}
                            control={control}
                            handleNext={handleNext}
                            firstName={firstName}
                            lastName={lastName}
                            email={email}
                            phoneNumber={phoneNumber}
                        />
                    </TabPanel>
                    <TabPanel value={currentTab} index={2}>
                        <Confirmation
                            sendForm={sendForm}
                            open={open}
                            setOpen={setOpen}
                            setValue={setValue}
                            email={email}
                        />
                    </TabPanel>
                    <Stack
                        gap={3}
                        className={classes.btn_wrapper}
                        sx={{
                            margin: '50px 0 50px 0',
                            justifyContent: 'flex-end',
                        }}
                    >
                        {currentTab !== 2 && (
                            <button
                                onClick={handleNext}
                                className={clsx(
                                    t('form.next') === 'Next' ? classes.en_btn : classes.fr_btn,
                                    classes.btn,
                                    nextButtonClassName,
                                )}
                                disabled={currentTab === 0 ? !(availableTimes.includes(timeWatcher) && !errors.date && !errors.time) : !isValid}
                            >
                                {t('form.next')}
                            </button>
                        )}
                    </Stack>
                </Box>
            </Box>
        </Box>
    )
}

export default Appointment